import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UserPlus, Edit2, Crown, Trash2, Mail, KeyRound, Copy, UserCog, User } from "lucide-react";
import { useBrutalConfirm } from "@/components/BrutalConfirm";

// iter84.70 — Participants expansion panel for a single league row,
// extracted from SiteAdmin.jsx. Owns the Add Crewmate form + per-player
// rename / promote / co-admin / remove actions.
export default function LeagueParticipantsPanel({ league, participants, onParticipantsChanged, onLeaguesReload }) {
    const lg = league;
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [addPlayerOpen, setAddPlayerOpen] = useState(false);
    const [addPlayerForm, setAddPlayerForm] = useState({ username: "", email: "", sendEmail: true });
    const [addPlayerBusy, setAddPlayerBusy] = useState(false);
    // iter87.152 — `window.confirm` is silently blocked on mobile inside
    // some Radix Dialog stacks; swap for the neo-brutalist AlertDialog
    // wrapper. Matches iter87.134/.135/.136 fixes on the league-admin side.
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();

    const refreshParticipants = async () => {
        const p = await api.siteListParticipants(lg.id);
        onParticipantsChanged(p);
    };

    const openAddPlayer = () => {
        setAddPlayerOpen(true);
        setAddPlayerForm({ username: "", email: "", sendEmail: true });
    };
    const closeAddPlayer = () => {
        setAddPlayerOpen(false);
        setAddPlayerBusy(false);
    };
    const submitAddPlayer = async () => {
        const username = addPlayerForm.username.trim();
        const email = addPlayerForm.email.trim();
        if (!username) { toast.error("Username is required"); return; }
        setAddPlayerBusy(true);
        try {
            const r = await api.siteAddParticipant(lg.id, {
                username,
                email: email || null,
                send_invite_email: !!(email && addPlayerForm.sendEmail),
            });
            toast.success(`Added ${r.participant.username}`);
            if (r.late_join) {
                toast("Heads up — draft already happened, this crewmate has no teams yet.",
                    { icon: "⚠️", duration: 8000 });
            }
            if (email && addPlayerForm.sendEmail && r.email_error) {
                toast.error("Player added, but invite email failed: " + r.email_error);
            }
            await refreshParticipants();
            onLeaguesReload();
            closeAddPlayer();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to add player");
        }
        setAddPlayerBusy(false);
    };

    const saveRename = async (pid) => {
        if (!renameValue.trim()) return;
        try {
            await api.siteRenameParticipant(pid, { username: renameValue.trim() });
            toast.success("Renamed");
            setRenamingId(null); setRenameValue("");
            await refreshParticipants();
        } catch { toast.error("Failed"); }
    };

    const togglePromote = async (pid, currentIsAdmin) => {
        try {
            await api.sitePromoteParticipant(pid, { is_admin: !currentIsAdmin });
            toast.success(currentIsAdmin ? "Demoted" : "Promoted to rivalry admin");
            await refreshParticipants();
        } catch { toast.error("Failed"); }
    };

    // iter35a — Promote / demote co-admin (separate from primary-admin toggle)
    const toggleCoAdmin = async (pid, currentIsCoAdmin) => {
        try {
            if (currentIsCoAdmin) {
                await api.siteDemoteCoAdmin(lg.id, pid);
                toast.success("Demoted co-admin");
            } else {
                await api.sitePromoteCoAdmin(lg.id, pid);
                toast.success("Promoted to co-admin");
            }
            await refreshParticipants();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const removeParticipant = async (pid) => {
        const ok = await brutalConfirm({
            title: "Remove crewmate?",
            message: "Remove this crewmate from the rivalry. Drafted teams stay locked to the (now-orphaned) slot, so the leaderboard skips them but tournament math stays intact.",
            confirmLabel: "Remove",
            cancelLabel: "Cancel",
            tone: "danger",
        });
        if (!ok) return;
        try {
            await api.siteRemoveParticipant(pid);
            toast.success("Removed");
            await refreshParticipants();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    // Add Crewmate — site admin direct insert. Cap = min(20, team_count).
    const cap = Math.min(20, lg.tournament_summary?.team_count || 20);
    const used = participants.length;
    const remaining = Math.max(0, cap - used);

    return (
        <div className="mt-4 pt-4 border-t-2 border-cyber/20 space-y-3" data-testid={`league-${lg.id}-participants`}>
            {confirmHost}
            <div
                className="brutal-card-light p-4 space-y-2 border-cyber/30 bg-gradient-to-r from-cyber/[0.04] to-transparent"
                data-testid={`add-player-card-${lg.id}`}
            >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 flex items-center justify-center bg-cyber/10 border border-cyber/40 rounded-lg shrink-0">
                            <UserPlus className="w-4 h-4 text-cyber" />
                        </div>
                        <span className="font-display font-black text-sm uppercase tracking-tight">Add crewmate</span>
                        <span className="pill bg-black/40 border border-white/[0.08] text-[10px]" data-testid={`add-player-slots-${lg.id}`}>
                            {remaining} / {cap} slots left
                        </span>
                    </div>
                    {!addPlayerOpen && (
                        <button
                            data-testid={`add-player-open-${lg.id}-btn`}
                            onClick={openAddPlayer}
                            disabled={remaining === 0}
                            className="brutal-btn-primary text-xs py-1.5 px-3 disabled:opacity-30"
                        >
                            <UserPlus className="w-3.5 h-3.5" /> Add
                        </button>
                    )}
                </div>
                {addPlayerOpen && (
                    <div className="space-y-2 pt-1" data-testid={`add-player-form-${lg.id}`}>
                        <div>
                            <label className="block text-[10px] uppercase font-bold tracking-wider text-zinc-500 mb-1">
                                Username <span className="text-blaze normal-case font-medium tracking-normal">— case-sensitive</span>
                            </label>
                            <input
                                data-testid={`add-player-username-input-${lg.id}`}
                                autoFocus
                                value={addPlayerForm.username}
                                onChange={(e) => setAddPlayerForm(f => ({ ...f, username: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === "Enter") submitAddPlayer(); if (e.key === "Escape") closeAddPlayer(); }}
                                placeholder="player_one"
                                maxLength={40}
                                className="brutal-input w-full py-1.5 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase font-bold tracking-wider text-zinc-500 mb-1">
                                Email <span className="text-zinc-600 normal-case font-medium tracking-normal">— optional</span>
                            </label>
                            <input
                                data-testid={`add-player-email-input-${lg.id}`}
                                type="email"
                                value={addPlayerForm.email}
                                onChange={(e) => setAddPlayerForm(f => ({ ...f, email: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === "Enter") submitAddPlayer(); if (e.key === "Escape") closeAddPlayer(); }}
                                placeholder="player@example.com"
                                className="brutal-input w-full py-1.5 text-sm"
                            />
                        </div>
                        <label className={`flex items-center gap-2 text-xs ${addPlayerForm.email.trim() ? "text-zinc-300 cursor-pointer" : "text-zinc-600 cursor-not-allowed"}`}>
                            <input
                                type="checkbox"
                                data-testid={`add-player-send-email-checkbox-${lg.id}`}
                                checked={addPlayerForm.sendEmail && !!addPlayerForm.email.trim()}
                                disabled={!addPlayerForm.email.trim()}
                                onChange={(e) => setAddPlayerForm(f => ({ ...f, sendEmail: e.target.checked }))}
                                className="accent-cyber"
                            />
                            Email this crewmate their invite + join link
                        </label>
                        <div className="flex gap-2 pt-1">
                            <button
                                data-testid={`add-player-submit-${lg.id}-btn`}
                                onClick={submitAddPlayer}
                                disabled={addPlayerBusy || !addPlayerForm.username.trim()}
                                className="brutal-btn-primary text-xs py-1.5 px-3 disabled:opacity-30"
                            >
                                {addPlayerBusy ? "Adding…" : "Add crewmate"}
                            </button>
                            <button
                                data-testid={`add-player-cancel-${lg.id}-btn`}
                                onClick={closeAddPlayer}
                                className="brutal-btn-ghost text-xs py-1.5 px-3"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
            {participants.map(p => (
                <div
                    key={p.id}
                    className={`brutal-card-light p-3 flex flex-wrap items-start justify-between gap-2 transition-colors hover:border-cyber/30 ${
                        p.is_admin ? "border-cyber/40" : p.is_co_admin ? "border-volt/40" : ""
                    }`}
                >
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg border shrink-0 ${
                            p.is_admin
                                ? "bg-cyber/10 border-cyber/40"
                                : p.is_co_admin
                                    ? "bg-volt/10 border-volt/40"
                                    : "bg-black/40 border-white/[0.08]"
                        }`}>
                            {p.is_admin
                                ? <Crown className="w-4 h-4 text-cyber" />
                                : p.is_co_admin
                                    ? <UserCog className="w-4 h-4 text-volt" />
                                    : <User className="w-4 h-4 text-zinc-500" />}
                        </div>
                        {renamingId === p.id ? (
                            <>
                                <input
                                    data-testid={`rename-input-${p.id}`}
                                    value={renameValue}
                                    onChange={(e) => setRenameValue(e.target.value)}
                                    className="brutal-input py-1 px-2 text-sm w-40" autoFocus />
                                <button data-testid={`rename-save-${p.id}-btn`} onClick={() => saveRename(p.id)} className="brutal-btn-primary text-[10px] py-1 px-2">Save</button>
                                <button onClick={() => setRenamingId(null)} className="brutal-btn-ghost text-[10px] py-1 px-2">Cancel</button>
                            </>
                        ) : (
                            <>
                                <span className="font-display font-black text-sm tracking-tight">{p.username}</span>
                                {p.is_admin && (
                                    <span className="pill bg-cyber/15 border border-cyber/40 text-cyber text-[9px] py-0.5 px-1.5" title="Primary admin">
                                        <Crown className="w-2.5 h-2.5" /> Admin
                                    </span>
                                )}
                                {p.is_co_admin && !p.is_admin && (
                                    <span className="pill bg-volt/15 border-volt/40 text-volt border text-[9px] py-0.5 px-1.5" data-testid={`coadmin-pill-${p.id}`}>
                                        <UserCog className="w-2.5 h-2.5" /> Co-admin
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                    {renamingId !== p.id && (
                        <div className="flex gap-1 flex-wrap">
                            <button data-testid={`rename-${p.id}-btn`} onClick={() => { setRenamingId(p.id); setRenameValue(p.username); }} className="brutal-btn-ghost text-[10px] py-1 px-2">
                                <Edit2 className="w-3 h-3" />
                            </button>
                            <button data-testid={`promote-${p.id}-btn`} onClick={() => togglePromote(p.id, p.is_admin)} className="brutal-btn-ghost text-[10px] py-1 px-2">
                                <Crown className={`w-3 h-3 ${p.is_admin ? "text-cyber" : ""}`} /> {p.is_admin ? "Demote" : "Promote"}
                            </button>
                            {!p.is_admin && (
                                <button data-testid={`coadmin-toggle-${p.id}-btn`} onClick={() => toggleCoAdmin(p.id, p.is_co_admin)} className="brutal-btn-ghost text-[10px] py-1 px-2" title="Promote/demote as co-admin">
                                    <UserCog className={`w-3 h-3 ${p.is_co_admin ? "text-volt" : ""}`} /> {p.is_co_admin ? "Remove co-admin" : "Make co-admin"}
                                </button>
                            )}
                            <button data-testid={`remove-participant-${p.id}-btn`} onClick={() => removeParticipant(p.id)} className="brutal-btn-ghost text-[10px] py-1 px-2">
                                <Trash2 className="w-3 h-3 text-heat" />
                            </button>
                        </div>
                    )}
                    {/* Recovery info — email + personal code so site admin can help when
                        a rivalry admin loses the unique id. Read-only here; edits live in
                        the Rivalry Admin → Crewmates tab. */}
                    <div
                        data-testid={`participant-recovery-${p.id}`}
                        className="basis-full pt-2 mt-1 border-t border-[#222] flex flex-wrap gap-x-4 gap-y-1 text-[11px]"
                    >
                        <div className="flex items-center gap-1.5 min-w-0">
                            <Mail className="w-3 h-3 text-zinc-500 shrink-0" />
                            <span
                                data-testid={`participant-email-${p.id}`}
                                className={`font-mono truncate ${p.email ? "text-zinc-300" : "text-zinc-600 italic"}`}
                            >
                                {p.email || "no email"}
                            </span>
                            {p.email && (
                                <button
                                    data-testid={`copy-email-${p.id}-btn`}
                                    onClick={() => { navigator.clipboard?.writeText(p.email); toast.success("Email copied"); }}
                                    className="brutal-btn-ghost text-[9px] px-1.5 py-0.5"
                                    title="Copy email"
                                >
                                    <Copy className="w-2.5 h-2.5" />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-1.5 min-w-0">
                            <KeyRound className="w-3 h-3 text-zinc-500 shrink-0" />
                            <span
                                data-testid={`participant-code-${p.id}`}
                                className={`font-mono tracking-[0.1em] ${p.personal_code ? "text-volt font-bold" : "text-zinc-600 italic"}`}
                            >
                                {p.personal_code || "no code"}
                            </span>
                            {p.personal_code && (
                                <button
                                    data-testid={`copy-code-${p.id}-btn`}
                                    onClick={() => { navigator.clipboard?.writeText(p.personal_code); toast.success("Code copied"); }}
                                    className="brutal-btn-ghost text-[9px] px-1.5 py-0.5"
                                    title="Copy personal code"
                                >
                                    <Copy className="w-2.5 h-2.5" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            ))}
            {participants.length === 0 && (
                <div className="brutal-card-light p-6 text-center border-dashed">
                    <p className="text-zinc-500 text-sm">No participants yet.</p>
                    <p className="text-[11px] text-zinc-600 mt-1">Use the <strong className="text-cyber">Add crewmate</strong> card above.</p>
                </div>
            )}
        </div>
    );
}
